/**
 * La mappa delle aziende per chi apre un link condiviso dalla console (`/mappa/:codice`, 27/10/2026). Fuori
 * dall'accesso: nessun login, nessun menu della console. Il codice del link è l'unica credenziale e i dati sono solo
 * quelli pubblici che il server lascia passare (`mappaAziendePubblica`).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { codiceCondivisioneValido, type RispostaMappaPubblica } from '@shared/admin/mappaAziende';
import { tokens } from '../theme';
import { leggiMappaCondivisa, motivoErrore } from '../services/mappaAziendeService';
import { COLORI_MAPPA } from '../components/mappa/aspetto';
import { useAggiornamentoPeriodico } from '../components/mappa/aggiornamento';
import { useFormatiMappa } from '../components/mappa/formati';
import { VistaMappaAziende } from '../components/mappa/VistaMappaAziende';

type Stato = { tipo: 'carica' } | { tipo: 'errore'; motivo: string } | { tipo: 'pronta'; risposta: RispostaMappaPubblica };

const Avviso: React.FC<{ titolo: string; testo: string; azione?: React.ReactNode }> = ({ titolo, testo, azione }) => (
  <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: COLORI_MAPPA.mare, px: 2 }}>
    <Box sx={{ maxWidth: 420, textAlign: 'center' }}>
      <Typography component="h1" sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.6rem', letterSpacing: '-0.015em', mb: 1 }}>{titolo}</Typography>
      <Typography sx={{ color: tokens.color.ash, fontSize: '0.95rem', lineHeight: 1.55 }}>{testo}</Typography>
      {azione && <Box sx={{ mt: 2.5 }}>{azione}</Box>}
    </Box>
  </Box>
);

export const MappaCondivisaPage: React.FC = () => {
  const { t } = useTranslation('mappa');
  const { codice } = useParams<{ codice: string }>();
  const formati = useFormatiMappa();
  const [stato, setStato] = useState<Stato>({ tipo: 'carica' });
  const [tentativo, setTentativo] = useState(0);

  useEffect(() => {
    document.title = t('pagina.titolo_condivisa');
  }, [t]);

  useEffect(() => {
    let vivo = true;
    if (!codiceCondivisioneValido(codice)) {
      setStato({ tipo: 'pronta', risposta: { attiva: false } });
      return;
    }
    setStato({ tipo: 'carica' });
    leggiMappaCondivisa(codice)
      .then((risposta) => { if (vivo) setStato({ tipo: 'pronta', risposta }); })
      .catch((e: unknown) => {
        console.error('[MappaCondivisaPage] lettura della mappa non riuscita', e);
        if (vivo) setStato({ tipo: 'errore', motivo: motivoErrore(e) });
      });
    return () => { vivo = false; };
  }, [codice, tentativo]);

  // Ogni minuto, in silenzio. Un link ritirato nel frattempo porta alla pagina «non più attivo».
  const aggiornaInSilenzio = useCallback(async () => {
    if (!codiceCondivisioneValido(codice)) return;
    try {
      const risposta = await leggiMappaCondivisa(codice);
      setStato({ tipo: 'pronta', risposta });
    } catch (e) {
      console.warn('[MappaCondivisaPage] aggiornamento automatico non riuscito', e);
    }
  }, [codice]);
  useAggiornamentoPeriodico(aggiornaInSilenzio, stato.tipo === 'pronta' && stato.risposta.attiva);

  if (stato.tipo === 'carica') {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: COLORI_MAPPA.mare }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: tokens.color.ash }}>
          <CircularProgress size={20} />
          <Typography sx={{ fontSize: '0.9rem' }}>{t('pagina.caricamento')}</Typography>
        </Box>
      </Box>
    );
  }
  if (stato.tipo === 'errore') {
    return (
      <Avviso
        titolo={t('pagina.titolo_condivisa')}
        testo={t('pagina.errore', { motivo: stato.motivo })}
        azione={<Button variant="outlined" onClick={() => setTentativo((n) => n + 1)} sx={{ textTransform: 'none', minHeight: 44 }}>{t('pagina.riprova')}</Button>}
      />
    );
  }
  const risposta = stato.risposta;
  if (!risposta.attiva) return <Avviso titolo={t('condivisa.non_attivo_titolo')} testo={t('condivisa.non_attivo_testo')} />;

  return (
    <Box sx={{ height: '100dvh', width: '100%' }}>
      <VistaMappaAziende
        modo="condivisa"
        aziende={risposta.aziende}
        giorniPersoneAttive={risposta.giorni_persone_attive}
        minutiAdesso={risposta.minuti_adesso}
        intestazione={
          <Box>
            <Typography
              component="h1"
              sx={{
                fontFamily: tokens.font.display,
                fontWeight: 600,
                fontSize: { xs: '1.4rem', md: '2.1rem' },
                lineHeight: 1.08,
                letterSpacing: '-0.02em',
                color: tokens.color.graphite,
                maxWidth: 560,
                fontVariationSettings: '"WONK" 0, "SOFT" 30',
              }}
            >
              {t('pagina.titolo_condivisa')}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: tokens.color.ash, mt: 0.75, maxWidth: 520, display: { xs: 'none', md: 'block' } }}>
              {t('pagina.descrizione')}
            </Typography>
          </Box>
        }
        piede={
          <>
            <Typography>{t('pagina.aggiornata', { quando: formati.dataOra(risposta.calcolata_il) })}</Typography>
            <Typography>{t('pagina.valida_fino', { data: formati.giorno(risposta.scade_il) })}</Typography>
            <Typography>{t('pagina.fonte_confini')}</Typography>
          </>
        }
      />
    </Box>
  );
};
